import { useEffect, useState } from "react";

const PAGE_SIZE = 50;

/** What scrolls the node: its nearest ancestor with a vertical scrollbar. */
const scrollParentOf = (node: HTMLElement): HTMLElement | null => {
  for (let el = node.parentElement; el; el = el.parentElement) {
    const { overflowY } = getComputedStyle(el);
    if (overflowY === "auto" || overflowY === "scroll") return el;
  }
  return null;
};

/**
 * Infinite scroll over rows the page already holds: the first page is drawn,
 * and the next one is added each time the sentinel after the last row comes
 * within a screen of the bottom of whatever scrolls it. Another resetKey
 * (filters, sort) starts again from the first page at the top; new data under
 * the same key (a refetch, a saved manager) keeps the place.
 */
const useInfiniteRows = <T>(
  rows: T[],
  resetKey: string,
  pageSize = PAGE_SIZE,
) => {
  const [sentinel, setSentinel] = useState<HTMLElement | null>(null);
  const [count, setCount] = useState(pageSize);

  useEffect(() => {
    setCount(pageSize);
    const scroller = sentinel && scrollParentOf(sentinel);
    if (scroller) scroller.scrollTop = 0;
    // Not on the sentinel: it is only where to scroll, and it mounts once.
  }, [resetKey, pageSize]);

  const hasMore = count < rows.length;

  // Recreated after every page: a fresh observer reports at once, so a page
  // that leaves the sentinel in sight is followed by the next one.
  useEffect(() => {
    if (!sentinel || !hasMore) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setCount((prev) => prev + pageSize);
      },
      { root: scrollParentOf(sentinel), rootMargin: "0px 0px 100% 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinel, hasMore, count, pageSize]);

  return {
    visibleRows: hasMore ? rows.slice(0, count) : rows,
    /** Ref for an empty element right after the last row. */
    sentinelRef: setSentinel,
  };
};

export default useInfiniteRows;
